import { useState } from 'react';

import styled from 'styled-components';

import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';
import { useEvent } from '@hooks/use-event';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { Switch, SwitchProps } from './switch';

export type SwitchGroupOptionProps = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

export type SwitchGroupProps = {
  label: string;
  hideLabel?: boolean;
  options: Array<SwitchGroupOptionProps>;
  defaultValue?: string[];
  value?: string[];
  onChange?(values: string[]): void;
  isDisabled?: boolean;
  error?: string;
  size?: SwitchProps['size'];
  toggleAllLabel?: string;
};

export const SwitchGroup = ({
  label,
  hideLabel,
  options,
  defaultValue = [],
  value,
  onChange,
  isDisabled = false,
  error,
  size = 'sm',
  toggleAllLabel,
}: SwitchGroupProps) => {
  const isControlled = value !== undefined;
  const [localValues, setLocalValues] = useState<string[]>(defaultValue);
  const selectedValues = isControlled ? value : localValues;
  const { t } = useScopedTranslate(locale);

  const handleToggle = useEvent((optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((value) => value !== optionValue)
      : [...selectedValues, optionValue];
    setLocalValues(newValues);
    onChange?.(newValues);
  });

  const allEnabled = options.every((option) => selectedValues.includes(option.value));

  const handleToggleAll = useEvent(() => {
    const newValues = allEnabled ? [] : options.map((option) => option.value);
    setLocalValues(newValues);
    onChange?.(newValues);
  });

  const labelElement = <FormLabel text={label} />;

  return (
    <Stack space={2}>
      {hideLabel ? <VisuallyHidden>{labelElement}</VisuallyHidden> : labelElement}
      <SwitchOptionsContainer>
        <Switch
          label={toggleAllLabel ?? t('toggleAllLabel')}
          value={allEnabled}
          onChange={handleToggleAll}
          isDisabled={isDisabled}
          size={size}
        />
        {options.map((option) => (
          <Switch
            key={option.value}
            label={option.label}
            value={selectedValues.includes(option.value)}
            onChange={() => handleToggle(option.value)}
            isDisabled={isDisabled || option.isDisabled}
            size={size}
          />
        ))}
      </SwitchOptionsContainer>
      <FormError text={error} />
    </Stack>
  );
};

const SwitchOptionsContainer = styled(Stack).attrs({ space: 2 })({
  svg: {
    marginTop: pxToRem(2),
  },
  button: {
    height: 'auto',
    minHeight: pxToRem(24),
    alignItems: 'flex-start',
    label: {
      textAlign: 'left',
      span: {
        font: theme.font.regular14,
        transition: 'color 200ms ease-out',
      },
    },
    '&:not([data-headlessui-state~="checked"])': {
      'label>span': {
        color: theme.color.textHelper,
      },
    },
  },
});

const locale = {
  en: {
    toggleAllLabel: 'Enable/Disable all',
  },
};
